/**
 * TrustGate Guided Flow Engine
 * 
 * BOUNDARY: State machine for intent construction only
 * - No policy logic
 * - No authority decisions
 * - No Firestore writes
 * - Calls createActionRequest, never executes directly
 */

import type { FlowPreset, GuidedFlowState, FlowStep } from './types';
import { createActionRequest } from '../data/TrustGateActionService';

export class GuidedFlowEngine {
  private state: GuidedFlowState;
  private preset: FlowPreset;

  constructor(preset: FlowPreset) {
    this.preset = preset;
    this.state = {
      presetId: preset.id,
      currentStepIndex: 0,
      collectedParams: {},
      errors: {},
      isValid: false
    };
  }

  /**
   * Get current state (read-only)
   */
  getState(): Readonly<GuidedFlowState> {
    return { ...this.state };
  }

  /**
   * Get current step
   */
  getCurrentStep(): FlowStep | null {
    if (this.state.currentStepIndex >= this.preset.steps.length) {
      return null;
    }
    return this.preset.steps[this.state.currentStepIndex];
  }

  /**
   * Get all steps
   */
  getAllSteps(): FlowStep[] {
    return [...this.preset.steps];
  }

  /**
   * Get flow metadata
   */
  getFlowMetadata() {
    return {
      id: this.preset.id,
      name: this.preset.name,
      description: this.preset.description,
      industry: this.preset.industry,
      category: this.preset.category,
      requiresAuthority: this.preset.requiresAuthority,
      auditClass: this.preset.auditClass
    };
  }

  /**
   * Update a field value and validate
   */
  updateField(fieldName: string, value: any): void {
    // Update collected params
    this.state.collectedParams[fieldName] = value;

    // Clear previous error for this field
    delete this.state.errors[fieldName];

    // Validate the field
    const currentStep = this.getCurrentStep();
    if (currentStep && currentStep.fieldName === fieldName) {
      if (currentStep.validation) {
        const error = currentStep.validation(value);
        if (error) {
          this.state.errors[fieldName] = error;
        }
      }
    }

    // Revalidate overall state
    this.validateState();
  }

  /**
   * Validate current state
   */
  private validateState(): void {
    const errors: Record<string, string> = {};
    
    // Validate all required fields up to current step
    for (let i = 0; i <= this.state.currentStepIndex; i++) {
      const step = this.preset.steps[i];
      if (!step) continue;

      const value = this.state.collectedParams[step.fieldName];
      
      // Check required
      if (step.required && (value === undefined || value === null || value === '')) {
        errors[step.fieldName] = `${step.label} is required`;
        continue;
      }

      // Run custom validation
      if (step.validation && value !== undefined && value !== null && value !== '') {
        const error = step.validation(value);
        if (error) {
          errors[step.fieldName] = error;
        }
      }
    }

    this.state.errors = errors;
    this.state.isValid = Object.keys(errors).length === 0;
  }

  /**
   * Move to next step
   */
  nextStep(): boolean {
    const currentStep = this.getCurrentStep();
    if (!currentStep) return false;

    // Validate current step
    this.validateState();
    if (!this.state.isValid) {
      return false;
    }

    // Move to next step
    if (this.state.currentStepIndex < this.preset.steps.length - 1) {
      this.state.currentStepIndex++;
      return true;
    }

    return false;
  }

  /**
   * Move to previous step
   */
  previousStep(): boolean {
    if (this.state.currentStepIndex > 0) {
      this.state.currentStepIndex--;
      return true;
    }
    return false;
  }

  /**
   * Check if can proceed to next step
   */
  canProceed(): boolean {
    return this.state.isValid;
  }

  /**
   * Check if on final step
   */
  isLastStep(): boolean {
    return this.state.currentStepIndex === this.preset.steps.length - 1;
  }

  /**
   * Get progress percentage
   */
  getProgress(): number {
    if (this.preset.steps.length === 0) return 0;
    return Math.round((this.state.currentStepIndex / this.preset.steps.length) * 100);
  }

  /**
   * Submit the request - calls createActionRequest
   * 
   * CRITICAL: This NEVER executes the action
   * It only creates a governed request that requires authority
   */
  async submit(userId: string): Promise<{ success: boolean; requestId?: string; error?: string }> {
    // Final validation
    this.validateState();
    if (!this.state.isValid) {
      return {
        success: false,
        error: 'Please fix validation errors before submitting'
      };
    }

    try {
      // Call createActionRequest (Cloud Function)
      // This creates a governed request, does NOT execute
      const result = await createActionRequest({
        amount: this.state.collectedParams.amount ? 
                parseFloat(this.state.collectedParams.amount) * 100 : 
                0, // Convert dollars to cents, default to 0
        currency: this.state.collectedParams.currency || 'USD',
        destination: this.state.collectedParams.targetSystem || 
                    this.state.collectedParams.destination ||
                    this.state.collectedParams.recipient ||
                    this.state.collectedParams.toAccount ||
                    'system',
        memo: JSON.stringify({
          flowPreset: this.preset.id,
          userId,
          params: this.state.collectedParams,
          context: this.state.collectedParams.riskContext || 
                  this.state.collectedParams.justification ||
                  this.state.collectedParams.purpose ||
                  this.state.collectedParams.description ||
                  'Guided flow submission'
        })
      });

      if (result.ok && result.requestId) {
        this.state.submittedRequestId = result.requestId;
        return {
          success: true,
          requestId: result.requestId
        };
      }

      return {
        success: false,
        error: result.reasonCode || 'Failed to create action request'
      };

    } catch (error) {
      console.error('❌ GuidedFlowEngine.submit error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Reset the flow to start
   */
  reset(): void {
    this.state = {
      presetId: this.preset.id,
      currentStepIndex: 0,
      collectedParams: {},
      errors: {},
      isValid: false
    };
  }

  /**
   * Get submitted request ID (if submitted)
   */
  getSubmittedRequestId(): string | undefined {
    return this.state.submittedRequestId;
  }
}
